import { useMutation, useQuery } from "@apollo/client/react";
import {
  DndContext,
  type DragEndEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-hot-toast";
import { FiPlus, FiX } from "react-icons/fi";
import {
  DeletePhonicsAudioDocument,
  GetPhonemesGroupedDocument,
  GetPhonicsAudiosByPhonemeDocument,
  PhonicsAudioLabel,
  ReorderPhonicsAudioDocument,
  UpdatePhonicsAudioDocument,
  UploadPhonicsAudioDocument,
} from "../../../generated/graphql";
import { SortablePhonicsAudioCard } from "./SortablePhonicsAudioCard";
import { WaveformTrimmer } from "./WaveformTrimmer";

interface Voice {
  id: string;
  phonemeId: string;
  voiceVariant: number;
  label: PhonicsAudioLabel;
  filename: string;
  url: string;
  duration?: number | null;
}

interface VoicesModalProps {
  phonemeId: string;
  phoneme: string;
  onClose: () => void;
}

export function VoicesModal({ phonemeId, phoneme, onClose }: VoicesModalProps) {
  const [playingId, setPlayingId] = useState<string | null>(null);
  const [changingLabelId, setChangingLabelId] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadLabel, setUploadLabel] = useState<PhonicsAudioLabel>(PhonicsAudioLabel.Card);
  const [localVoices, setLocalVoices] = useState<Voice[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const { data, loading, error } = useQuery(GetPhonicsAudiosByPhonemeDocument, {
    variables: { phonemeId },
    fetchPolicy: "cache-first",
  });

  const voices = useMemo(() => {
    if (error) {
      toast.error(`Failed to load voices: ${error.message}`);
      return [];
    }
    return (data?.phonicsAudiosByPhoneme || []) as Voice[];
  }, [data, error]);

  // sync local voices with server data when not dragging
  useEffect(() => {
    if (!isDragging) {
      const sorted = [...voices].sort((a, b) => a.voiceVariant - b.voiceVariant);
      setLocalVoices(sorted);
    }
  }, [voices, isDragging]);

  const [deletePhonicsAudio] = useMutation(DeletePhonicsAudioDocument, {
    refetchQueries: [
      { query: GetPhonicsAudiosByPhonemeDocument, variables: { phonemeId } },
      // phoneme type null in variables, to match apollo normalization with the root query ( avoid re render )
      { query: GetPhonemesGroupedDocument, variables: { phonemeType: null } },
    ],
    onCompleted: () => {
      toast.success("Voice deleted successfully");
      // onUpdate();
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const [updatePhonicsAudio] = useMutation(UpdatePhonicsAudioDocument, {
    refetchQueries: [{ query: GetPhonicsAudiosByPhonemeDocument, variables: { phonemeId } }],
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const [uploadPhonicsAudio, { loading: uploading }] = useMutation(UploadPhonicsAudioDocument, {
    refetchQueries: [
      { query: GetPhonicsAudiosByPhonemeDocument, variables: { phonemeId } },
      { query: GetPhonemesGroupedDocument, variables: { phonemeType: null } },
    ],
    onCompleted: () => {
      toast.success("Voice variant uploaded successfully");
      setIsUploading(false);
      setUploadFile(null);
      setPendingFile(null);
      // onUpdate();
    },
    onError: (error) => {
      toast.error(`Upload failed: ${error.message}`);
    },
  });

  const [reorderPhonicsAudio] = useMutation(ReorderPhonicsAudioDocument, {
    refetchQueries: [{ query: GetPhonicsAudiosByPhonemeDocument, variables: { phonemeId } }],
    onError: (error) => {
      toast.error(`Failed to reorder: ${error.message}`);
      // revert to server state on error
      const sorted = [...voices].sort((a, b) => a.voiceVariant - b.voiceVariant);
      setLocalVoices(sorted);
    },
  });

  // voice variant is scoped per label
  const nextVoiceVariant = useMemo(() => {
    const labelVoices = voices.filter((v) => v.label === uploadLabel);
    if (labelVoices.length === 0) return 1;
    return Math.max(...labelVoices.map((v) => v.voiceVariant)) + 1;
  }, [voices, uploadLabel]);

  // handler for voice preview
  const handleTogglePlay = (voiceId: string) => {
    if (playingId === voiceId) {
      setPlayingId(null);
    } else {
      setPlayingId(voiceId);
    }
  };

  const handleDelete = async (voiceId: string) => {
    if (!confirm("Are you sure you want to delete this voice?")) return;

    try {
      await deletePhonicsAudio({ variables: { id: voiceId } });
    } catch (error) {
      console.error("Failed to delete voice:", error);
    }
  };

  const handleLabelChange = async (voiceId: string, newLabel: "card" | "sound") => {
    setChangingLabelId(voiceId);
    try {
      await updatePhonicsAudio({
        variables: { input: { id: voiceId, label: newLabel as PhonicsAudioLabel } },
      });
      toast.success(`Label changed to "${newLabel}"`);
    } catch (error) {
      console.error("Failed to change label:", error);
    } finally {
      setChangingLabelId(null);
    }
  };

  const handleUploadClick = () => {
    setIsUploading(true);
  };

  const handleCancelUpload = () => {
    setIsUploading(false);
    setUploadFile(null);
    setPendingFile(null);
    setUploadLabel(PhonicsAudioLabel.Card);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(null);
      setPendingFile(file);
    }
  };

  const handleTrimConfirm = (trimmedFile: File) => {
    setPendingFile(null);
    setUploadFile(trimmedFile);
  };

  const handleTrimSkip = () => {
    setUploadFile(pendingFile);
    setPendingFile(null);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) {
      toast.error("Please select an audio file");
      return;
    }

    try {
      await uploadPhonicsAudio({
        variables: {
          file: uploadFile,
          input: {
            phonemeId,
            voiceVariant: nextVoiceVariant,
            label: uploadLabel,
          },
        },
      });
    } catch (error) {
      console.error("Upload failed:", error);
    }
  };

  const handleDragStart = (_event: DragStartEvent) => {
    setIsDragging(true);
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setIsDragging(false);

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = localVoices.findIndex((v) => v.id === active.id);
    const newIndex = localVoices.findIndex((v) => v.id === over.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // optimistic update
    const reorderedVoices = arrayMove(localVoices, oldIndex, newIndex);
    setLocalVoices(reorderedVoices);

    const orderedIds = reorderedVoices.map((v) => v.id);
    try {
      await reorderPhonicsAudio({
        variables: {
          phonemeId,
          orderedIds,
        },
      });
    } catch (error) {
      console.error("Failed to reorder voices:", error);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Phoneme: <span className="text-blue-600">{phoneme}</span>
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              {voices.length} voice {voices.length === 1 ? "variant" : "variants"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Voices List */}
        <div className="max-h-96 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-gray-500">Loading voices...</div>
            </div>
          ) : (
            <div className="space-y-3">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={localVoices.map((v) => v.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {localVoices.map((voice) => (
                    <SortablePhonicsAudioCard
                      key={voice.id}
                      voice={voice}
                      isPlaying={playingId === voice.id}
                      onTogglePlay={() => handleTogglePlay(voice.id)}
                      onDelete={() => handleDelete(voice.id)}
                      onLabelChange={(newLabel) => handleLabelChange(voice.id, newLabel)}
                      isChangingLabel={changingLabelId === voice.id}
                    />
                  ))}
                </SortableContext>
              </DndContext>

              {voices.length === 0 && !isUploading && (
                <div className="rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 py-8 text-center">
                  <p className="text-sm text-gray-500">No voice variants yet</p>
                  <p className="mt-1 text-xs text-gray-400">Click "Add Voice Variant" to upload</p>
                </div>
              )}

              {/* Upload Form */}
              {isUploading && (
                <div className="rounded-lg border-2 border-blue-300 bg-blue-50 p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="font-medium text-gray-900">
                      Upload Voice Variant {nextVoiceVariant}
                    </h3>
                    <button
                      type="button"
                      onClick={handleCancelUpload}
                      className="text-gray-400 hover:text-gray-600"
                      disabled={uploading}
                    >
                      <FiX size={20} />
                    </button>
                  </div>

                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Type</label>
                      <div className="mt-1 flex gap-4">
                        {([PhonicsAudioLabel.Card, PhonicsAudioLabel.Sound] as const).map((l) => (
                          <label key={l} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="label"
                              value={l}
                              checked={uploadLabel === l}
                              onChange={() => setUploadLabel(l)}
                              disabled={uploading}
                              className="text-blue-600"
                            />
                            <span className="text-sm text-gray-700">
                              {l === PhonicsAudioLabel.Card
                                ? "Card (letter name + sound)"
                                : "Sound only"}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Audio File <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="file"
                        accept="audio/*"
                        onChange={handleFileChange}
                        disabled={uploading}
                        className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:rounded-md file:border-0 file:bg-blue-600 file:px-4 file:py-2 file:text-sm file:font-medium file:text-white hover:file:bg-blue-700"
                      />
                      {uploadFile && !pendingFile && (
                        <p className="mt-1 text-xs text-green-700 font-medium">
                          ✓ Ready — {uploadFile.name}
                        </p>
                      )}
                    </div>

                    {pendingFile && (
                      <WaveformTrimmer
                        file={pendingFile}
                        onConfirm={handleTrimConfirm}
                        onCancel={handleTrimSkip}
                      />
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={handleCancelUpload}
                        disabled={uploading}
                        className="flex-1 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={handleUploadSubmit}
                        disabled={uploading || !uploadFile || !!pendingFile}
                        className="flex-1 rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                      >
                        {uploading ? "Uploading..." : "Upload"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4">
          <div className="flex gap-3">
            {!isUploading && (
              <button
                type="button"
                onClick={handleUploadClick}
                className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <FiPlus size={16} />
                Add Voice Variant
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-md bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
